import {Link} from 'react-router-dom';
import {Check} from 'lucide-react';
import {cn} from '@common-web/lib/utils';
import {POSTHOG_UNMASK_CLASS} from '@common-web/lib/analytics';
import {useEnrollSteps} from '@client/pages/public/enroll/enrollStepsContext';
import {STEP, STEP_LABELS} from '@client/pages/public/enroll/enrollModel';

export function ProgressTracker() {
  const {group, steps, currentStep, currentIndex, maxReachedIndex, isStepComplete} = useEnrollSteps();
  const base = `/${group.slug}`;

  return (
    <ol className={cn('flex flex-wrap gap-2', POSTHOG_UNMASK_CLASS)}>
      {steps.map((step, index) => {
        const isCurrent = step === currentStep;
        // Checkmark rules:
        // - Product steps: earned by completeness; stay checked when navigating back to edit.
        // - Review: earned once its requirement is met AND the user has advanced past it (landed +
        //   clicked Continue at least once), so it doesn't check early; persists afterward.
        // - Checkout / Protection / Confirmation: position-gated — checked only while the user is past them.
        let isDone: boolean;
        if (step === STEP.Review) {
          isDone = isStepComplete(step) && maxReachedIndex > index;
        } else if (
          step === STEP.Checkout ||
          step === STEP.Protection ||
          step === STEP.Confirmation
        ) {
          isDone = isStepComplete(step) && index < currentIndex;
        } else {
          isDone = isStepComplete(step);
        }
        // Gate-aware: a step is reachable only when every step before it is complete. Breaking an
        // earlier selection re-locks everything after it; step 1 (no predecessors) is always open.
        // Confirmation is terminal — you only reach it by submitting from Checkout, never by tracker
        // click (clicking it would fire the submission).
        const isNavigable =
          step !== STEP.Confirmation && steps.slice(0, index).every((s) => isStepComplete(s));

        const badge = (
          <span
            className={cn(
              'flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold',
              isDone ? 'bg-green-600 text-white' : isCurrent ? 'text-white' : 'bg-neutral-200 text-neutral-500',
            )}
            style={isCurrent ? {backgroundColor: 'var(--enroll-accent)'} : undefined}
          >
            {isDone ? <Check className='h-3 w-3' /> : index + 1}
          </span>
        );

        const innerClass = cn(
          'inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
          isCurrent
            ? 'font-semibold'
            : isNavigable
              ? 'text-neutral-700 hover:bg-neutral-100'
              : 'cursor-default text-neutral-400',
        );
        const currentStyle = isCurrent
          ? {
              backgroundColor: 'color-mix(in srgb, var(--enroll-accent) 15%, white)',
              color: 'var(--enroll-accent)',
            }
          : undefined;

        return (
          <li key={step}>
            {isNavigable ? (
              <Link to={`${base}/${step}`} className={innerClass} style={currentStyle}>
                {badge}
                {STEP_LABELS[step]}
              </Link>
            ) : (
              <span aria-disabled className={innerClass}>
                {badge}
                {STEP_LABELS[step]}
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
